import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  InputSignal,
  OutputRef,
  Signal,
  ViewEncapsulation,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { IonNav } from '@ionic/angular/standalone';
import { AlfredClientService } from './alfred-client.service';
import { OnInteraction } from './models/on-interaction/on-interaction.model';
import { Step } from './models/step/step.model';
import { OverviewComponent } from './overview/overview.component';
import { DirectQuestionHandlerComponent } from './question-handlers/direct-question-handler/direct-question-handler.component';
import { DirectStepService } from './question-handlers/direct-question-handler/direct-step.service';
import { StepInterface } from './question-handlers/direct-question-handler/step.interface';
import { QuestionnaireHandlerComponent } from './question-handlers/questionnaire-handler/questionnaire-handler.component';
import { Mode } from './shared/enums/mode.enum';
import { outputFromObservable } from '@angular/core/rxjs-interop';

/**
 * To include the alfred client into your app simply include
 * this component in your template.
 *
 * @example (minimal)
 * <lib-alfred-client></lib-alfred-client>
 *
 * @example (extended)
 * <lib-alfred-client [sessionId]="sessionId"
 *                    (finished)="onFinishRating()"
 *                    [showCloseBtn]="false">
 * </lib-alfred-client>
 */
@Component({
  selector: 'lib-alfred-client',
  templateUrl: './alfred-client.component.html',
  styleUrls: ['./alfred-client.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IonNav],
})
export class AlfredClientComponent implements AfterViewInit {
  public directQuestionService: DirectStepService = inject(DirectStepService);
  public alfredClientOutputService: AlfredClientService =
    inject(AlfredClientService);

  /**
   * Flag which determines in which mode the component is running.
   */
  public readonly mode: InputSignal<Mode | undefined> = input<Mode>();

  /**
   * The parent app can supply the session id to open the
   * questionnaire directly.
   *
   * Only usable in {@see Mode.API} mode.
   * (Optional)
   */
  public readonly sessionId: InputSignal<string | undefined> = input<
    string | undefined
  >();

  /**
   * The parent app can supply the question data which the clientComponent needs
   * to show.
   *
   * Only usable in {@see Mode.DIRECT_HANDLE} mode.
   */
  public readonly directQuestion: InputSignal<
    StepInterface | Step | undefined
  > = input<StepInterface | Step>();

  /**
   * Flag that allows the parent app to hide the close button
   * while filling in a questionnaire.
   */
  public readonly showCloseBtn: InputSignal<boolean | undefined> =
    input<boolean>();

  /**
   * Flag that allows the parent app to disable the close confirmation of the questionnaire.
   *
   * When this property is called the client will only call the {@see onCancelClick} and
   * {@see finished}.
   */
  public readonly showCloseConfirmation: InputSignal<boolean | undefined> =
    input<boolean>();

  constructor() {
    effect(() => {
      const mode: Mode | undefined = this.mode();
      if (mode !== undefined) {
        this.alfredClientOutputService.mode.set(mode);
      }
    });

    effect(() => {
      const show: boolean | undefined = this.showCloseBtn();
      if (show !== undefined) {
        this.alfredClientOutputService.showCloseButton.set(show);
      }
    });

    effect(() => {
      const show: boolean | undefined = this.showCloseConfirmation();
      if (show !== undefined) {
        this.alfredClientOutputService.showCloseConfirmation.set(show);
      }
    });

    effect(() => {
      const question: StepInterface | Step | undefined = this.directQuestion();
      if (question !== undefined) {
        this.directQuestionService.directQuestion = question;
      }
    });
  }

  /**
   * Event emitted by the thank-you page.
   */
  public readonly finished: OutputRef<void> = outputFromObservable(
    this.alfredClientOutputService.finished,
  );

  /**
   * Event which triggers when the event button is triggered
   */
  public readonly onNextClick: OutputRef<OnInteraction | undefined> =
    outputFromObservable(this.alfredClientOutputService.onNextClick);

  /**
   * Event which triggers when the event button is triggered
   */
  public readonly onPreviousClick: OutputRef<OnInteraction | undefined> =
    outputFromObservable(this.alfredClientOutputService.onPreviousClick);

  /**
   * Event which triggers when the cancel button it trigger and the users
   * continued after the warning dialog.
   */
  public readonly onCancelClick: OutputRef<void> = outputFromObservable(
    this.alfredClientOutputService.onCancelClick,
  );

  /**
   * The ion nav item that handles the navigation inside the
   * client component.
   */
  public readonly nav: Signal<IonNav> = viewChild.required(IonNav);

  /**
   * Check on which mode the client component needs to run
   * and execute the corresponding method.
   */
  public ngAfterViewInit(): Promise<void> {
    switch (this.alfredClientOutputService.mode()) {
      case Mode.DIRECT_HANDLE:
        return this.runDirectHandleMode();
      default:
        return this.runApiMode();
    }
  }

  /**
   * Running client component in Api mode.
   *
   * If a session id has been provided directly navigate to the
   * questionnaire handler.
   */
  private async runApiMode(): Promise<void> {
    const sessionId: string | undefined = this.sessionId();
    if (sessionId) {
      await this.nav().setRoot(QuestionnaireHandlerComponent, {
        sessionId: sessionId,
      });
    } else {
      await this.nav().setRoot(OverviewComponent);
    }
  }

  /**
   * Running client component in Direct Handle Mode.
   */
  private async runDirectHandleMode(): Promise<void> {
    await this.nav().setRoot(DirectQuestionHandlerComponent);
  }
}
